import { IStorageService } from "@interfaces/storage-service.interface";
import { getStorageService } from "@factories/storage-service-factory";
import { ContextType } from "@enums/context-type.enum";
import { getContextType } from "@session/session";
import { indicatesStorageFull } from "@storage/storage-utils";
import { addToTraceBuffer, clearTraceBuffer, getTraceBuffer, pushToTraceBuffer } from "@storage/trace-buffer";
import { shallowTracer } from "@app-services/shallow-tracer.service";

// This key used to be used to store error traces in local storage. Now it is only used
// to do a best-effort cleanup of the trace storage. Eventually this can be omitted.
const legacyErrorTracesKey = "AIRV_LOGS";

let traceBufferSynchronized: boolean = false;
let storageService: IStorageService | undefined = undefined;

export function persistTrace(trace: string) {
    pushToTraceBuffer(trace);

    try {
        persistTraceBuffer();
    }
    catch (err) {
        shallowTracer.warn("Error while persisting trace buffer", err);

        if (indicatesStorageFull(err)) {
            tryClearTraceStorage();
        }

        // Swallow.
    }
}

export function getAllStoredTraces(): string[] {
    const allTraces: string[] = [];

    Object.values(ContextType).forEach(contextType => {
        const contextTraces = getContextTraces(contextType as ContextType);
        allTraces.push(...contextTraces);
    });

    return allTraces.sort();
}

export function tryClearTraceStorage() {
    try {
        clearTraceStorage();
    }
    catch (err) {
        console.warn("Error while clearing trace storage", err);
        // Swallow.
    }
}

function getOrCreateStorageService(): IStorageService {
    if (!storageService) {
        storageService = getStorageService();
    }

    return storageService;
}

function syncTraceBuffer(): void {
    if (!traceBufferSynchronized) {
        // Apply the current logs on top of the existing ones.
        addToTraceBuffer(getContextTraces(getContextType()), 0);
        traceBufferSynchronized = true;
        shallowTracer.debug(`Synced trace buffer`);
    }
}

function persistTraceBuffer(): void {
    syncTraceBuffer();

    const storageService = getOrCreateStorageService();

    storageService.set(getTraceBufferKey(), getTraceBuffer());
    storageService.commit(); // Promise not awaited intentionally.
}

function clearTraceStorage() {
    // Trace to console directly in this method, as we are trying to cleanup traces here.

    console.debug("Clearing trace buffer");
    clearTraceBuffer();

    console.debug("Clearing trace storage");
    const storageService = getOrCreateStorageService();

    Object.values(ContextType).forEach(contextType => {
        storageService.remove(getContextTraceBufferKey(contextType));
    });

    storageService.remove(legacyErrorTracesKey);
    storageService.commit(); // Promise not awaited intentionally.
}

function getContextTraces(contextType: ContextType): string[] {
    return getOrCreateStorageService().get(getContextTraceBufferKey(contextType)) ?? [];
}

function getTraceBufferKey(): string {
    return getContextTraceBufferKey(getContextType());
}

function getContextTraceBufferKey(contextType: ContextType): string {
    return "airv-logs-" + contextType.toString();
}